import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  AcquisitionRecommendation,
  LeagueConnection,
  Player,
  RankedPlayer,
  RiskPreferences,
  SeasonOutlook,
} from '../../types/fantasy-football'
import {
  analyzePool,
  recommendAcquisitions,
  seasonOutlook,
  type EngineInput,
} from '../../lib/fantasy-analytics'
import { defaultParams, defaultRiskPreferences, type ParamMap } from '../../lib/fantasy-knowledge-base'
import {
  getLeagueOpponents,
  getParams,
  getRiskPreferences,
  getRosterIds,
  getSosTable,
  listConnections,
  listPlayers,
} from '../../lib/fantasy-api'

// One hook shared by every tab: loads the raw data, runs the analytics
// engine client-side, and hands back memoized results plus a refresh.

export interface FantasyState {
  loading: boolean
  players: Player[]
  rosterIds: string[]
  roster: Player[]
  connections: LeagueConnection[]
  prefs: RiskPreferences
  params: ParamMap
  engineInput: EngineInput | null
  ranked: RankedPlayer[]
  outlook: SeasonOutlook | null
  recommendations: AcquisitionRecommendation[]
  refresh: () => void
}

export function useFantasy(): FantasyState {
  const [loading, setLoading] = useState(true)
  const [players, setPlayers] = useState<Player[]>([])
  const [rosterIds, setRosterIds] = useState<string[]>([])
  const [connections, setConnections] = useState<LeagueConnection[]>([])
  const [prefs, setPrefs] = useState<RiskPreferences>(defaultRiskPreferences)
  const [params, setParams] = useState<ParamMap>(defaultParams)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([listPlayers(), getRosterIds(), getRiskPreferences(), getParams(), listConnections()])
      .then(([pool, roster, preferences, parameters, conns]) => {
        if (cancelled) return
        setPlayers(pool)
        setRosterIds(roster)
        setPrefs(preferences)
        setParams(parameters)
        setConnections(conns)
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  const refresh = useCallback(() => setReloadKey((k) => k + 1), [])

  const engineInput = useMemo<EngineInput | null>(() => {
    if (loading || players.length === 0) return null
    return {
      pool: players,
      rosterIds,
      prefs,
      params,
      sos: getSosTable(),
      leagueTeamCount: connections[0]?.teamCount ?? 10,
    }
  }, [loading, players, rosterIds, prefs, params, connections])

  const ranked = useMemo(() => (engineInput ? analyzePool(engineInput) : []), [engineInput])

  const outlook = useMemo(
    () => (engineInput && rosterIds.length > 0 ? seasonOutlook(engineInput, undefined, getLeagueOpponents()) : null),
    [engineInput, rosterIds],
  )

  const recommendations = useMemo(
    () => (engineInput ? recommendAcquisitions(engineInput, 5) : []),
    [engineInput],
  )

  const roster = useMemo(
    () => players.filter((p) => rosterIds.includes(p.id)),
    [players, rosterIds],
  )

  return {
    loading,
    players,
    rosterIds,
    roster,
    connections,
    prefs,
    params,
    engineInput,
    ranked,
    outlook,
    recommendations,
    refresh,
  }
}

export function fmtPts(v: number, digits = 1): string {
  return v.toFixed(digits)
}

export function fmtSigned(v: number, digits = 1): string {
  return `${v >= 0 ? '+' : ''}${v.toFixed(digits)}`
}

export const POSITION_TONES: Record<string, 'blue' | 'green' | 'amber' | 'purple' | 'slate' | 'red'> = {
  QB: 'red',
  RB: 'green',
  WR: 'blue',
  TE: 'amber',
  K: 'purple',
  DST: 'slate',
}
